<template>
    <div class="row q-col-gutter-md">
        <div class="col-12 col-md-8 offset-md-2">
            <q-card>
                <q-card-section>
                    <div class="text-h6">Ask AI about Calendar Settings</div>
                    <div class="text-body2 text-grey">
                        Find out what options are available and where to find
                        them.
                    </div>
                </q-card-section>

                <q-card-section class="q-pt-none">
                    <div
                        ref="messagesContainer"
                        class="q-pa-md"
                        style="
                            max-height: 400px;
                            overflow-y: auto;
                            background-color: #f9f9f9;
                            border-radius: 4px;
                            margin-bottom: 16px;
                        "
                    >
                        <q-chat-message
                            v-for="(msg, index) in messages"
                            :key="index"
                            :name="msg.name"
                            :text="
                                msg.sent
                                    ? [msg.text]
                                    : [renderMarkdown(msg.text)]
                            "
                            :sent="msg.sent"
                            :bg-color="msg.sent ? 'primary' : 'grey-3'"
                            :text-color="msg.sent ? 'white' : 'black'"
                            :text-html="!msg.sent"
                        >
                            <template v-slot:avatar>
                                <q-avatar
                                    :icon="msg.sent ? 'person' : 'smart_toy'"
                                    :color="msg.sent ? 'secondary' : 'primary'"
                                    text-color="white"
                                    :class="
                                        msg.sent
                                            ? 'q-message-avatar q-message-avatar--sent'
                                            : 'q-message-avatar q-message-avatar--received'
                                    "
                                />
                            </template>
                        </q-chat-message>
                        <div v-if="loading" class="q-pa-md row justify-start">
                            <q-spinner-dots color="primary" size="2em" />
                        </div>
                    </div>

                    <form @submit.prevent="askQuestion">
                        <div class="row items-center q-gutter-md">
                            <div class="col">
                                <q-input
                                    v-model="question"
                                    outlined
                                    dense
                                    placeholder="Ask your question here..."
                                    :disable="loading"
                                    autofocus
                                />
                            </div>
                            <div class="col-auto">
                                <q-btn
                                    color="primary"
                                    icon="send"
                                    type="submit"
                                    :loading="loading"
                                    :disable="!question.trim() || loading"
                                />
                            </div>
                        </div>
                    </form>
                </q-card-section>
            </q-card>
        </div>
    </div>
</template>

<script>
import { ref, nextTick } from "vue";
import { useQuasar } from "quasar";
import markdownIt from "markdown-it";

export default {
    name: "AiHelpChat",
    props: {
        aiUrl: {
            type: String,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const $q = useQuasar();
        const question = ref("");
        const loading = ref(false);
        const messagesContainer = ref(null);

        const md = markdownIt({
            html: false,
            linkify: true,
            typographer: true,
        });

        const renderMarkdown = (text) => {
            return md.render(text);
        };

        const messages = ref([
            {
                name: "AI Assistant",
                text: 'Hello! I am here to help you understand the PW-TOOLS calendar settings. You can ask me questions like: "Can I adjust how far in the future publishers can make bookings?"',
                sent: false,
            },
        ]);

        const scrollToBottom = () => {
            nextTick(() => {
                if (messagesContainer.value) {
                    messagesContainer.value.scrollTop =
                        messagesContainer.value.scrollHeight;
                }
            });
        };

        const askQuestion = async () => {
            if (!question.value.trim() || loading.value) return;

            const currentQuestion = question.value.trim();

            // Add user message
            messages.value.push({
                name: "You",
                text: currentQuestion,
                sent: true,
                role: "user",
                content: currentQuestion,
            });

            question.value = "";
            loading.value = true;
            scrollToBottom();

            // Prepare messages for API (only role and content)
            const apiMessages = messages.value
                .filter((m) => m.role)
                .map((m) => ({
                    role: m.role,
                    content: m.content,
                }));

            try {
                const response = await fetch(props.aiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": props.csrfToken,
                        Accept: "application/json",
                    },
                    body: JSON.stringify({ messages: apiMessages }),
                });

                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                // Add placeholder for AI message
                const aiMessageIndex = messages.value.length;
                messages.value.push({
                    name: "AI Assistant",
                    text: "",
                    sent: false,
                    role: "assistant",
                    content: "",
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    messages.value[aiMessageIndex].text += chunk;
                    messages.value[aiMessageIndex].content += chunk;
                    scrollToBottom();
                }
            } catch (error) {
                console.error(error);
                $q.notify({
                    color: "negative",
                    message: "Network error or AI service unavailable.",
                    icon: "error",
                });
                messages.value.push({
                    name: "System",
                    text: "Sorry, I encountered an error. Is Ollama running?",
                    sent: false,
                });
            } finally {
                loading.value = false;
                scrollToBottom();
            }
        };

        return {
            question,
            loading,
            messages,
            messagesContainer,
            askQuestion,
            renderMarkdown,
        };
    },
};
</script>
